import { useState } from 'react'
import Card from '../../components/ui/Card'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import IntegrationBadge from '../../components/ui/IntegrationBadge'

const bankDeposits = [
  { id: 'b1', date: '2026-01-26', description: 'SQ *GOSQ.COM REF4829573619', amount: 3245.00, matched: false, source: 'square' },
  { id: 'b2', date: '2026-01-25', description: 'SQ *GOSQ.COM REF9182736450', amount: 2987.00, matched: true, source: 'square' },
  { id: 'b3', date: '2026-01-24', description: 'DOORDASH INC PAYOUT', amount: 876.50, matched: false, source: 'doordash' },
  { id: 'b4', date: '2026-01-23', description: 'UBER *EATS PAYOUT', amount: 654.25, matched: false, source: 'ubereats' },
  { id: 'b5', date: '2026-01-22', description: 'GRUBHUB HOLDINGS ACH', amount: 523.80, matched: false, source: 'grubhub' },
]

const posBatches = [
  { id: 'p1', date: '2026-01-26', provider: 'square', batchId: 'sq_A8K2M9X1', grossAmount: 3412.50, netAmount: 3245.00, txnCount: 87, matched: false },
  { id: 'p2', date: '2026-01-25', provider: 'square', batchId: 'sq_B3N7P2Y4', grossAmount: 3150.00, netAmount: 2987.00, txnCount: 72, matched: true },
  { id: 'p3', date: '2026-01-24', provider: 'square', batchId: 'sq_C5Q8R1Z6', grossAmount: 2875.00, netAmount: 2730.00, txnCount: 65, matched: false },
]

const deliveryPayouts = [
  { id: 'd1', date: '2026-01-24', platform: 'doordash', payoutId: 'DD-20260124-A8K2M9X1', grossAmount: 1050.00, netAmount: 876.50, orderCount: 28, matched: false },
  { id: 'd2', date: '2026-01-23', platform: 'ubereats', payoutId: 'UE-f47ac10b-58cc', grossAmount: 780.00, netAmount: 654.25, orderCount: 22, matched: false },
  { id: 'd3', date: '2026-01-22', platform: 'grubhub', payoutId: 'GH-20260122-XYZABC12', grossAmount: 620.00, netAmount: 523.80, orderCount: 18, matched: false },
]

export default function Reconciliation() {
  const [selectedBank, setSelectedBank] = useState<string | null>(null)
  const [selectedSource, setSelectedSource] = useState<string | null>(null)

  const handleMatch = () => {
    if (selectedBank && selectedSource) {
      alert(`Matched ${selectedBank} with ${selectedSource}`)
      setSelectedBank(null)
      setSelectedSource(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reconciliation</h1>
          <p className="text-gray-500 mt-1">Match bank deposits with POS batches and delivery payouts</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="secondary">Auto-Match</Button>
          <Button disabled={!selectedBank || !selectedSource} onClick={handleMatch}>
            Match Selected
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-6">
        <Card>
          <p className="text-sm text-gray-500">Unmatched Deposits</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">
            {bankDeposits.filter((d) => !d.matched).length}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Unmatched POS Batches</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">
            {posBatches.filter((b) => !b.matched).length}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Unmatched Delivery Payouts</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">
            {deliveryPayouts.filter((p) => !p.matched).length}
          </p>
        </Card>
      </div>

      {/* Split View */}
      <div className="grid grid-cols-2 gap-6">
        {/* Bank Deposits */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Bank Deposits</h2>
          <div className="space-y-2">
            {bankDeposits.map((deposit) => (
              <div
                key={deposit.id}
                onClick={() => !deposit.matched && setSelectedBank(deposit.id)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  deposit.matched
                    ? 'bg-green-50 border-green-200 opacity-50 cursor-not-allowed'
                    : selectedBank === deposit.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <IntegrationBadge platform={deposit.source} size="sm" />
                    </div>
                    <p className="font-medium text-gray-900 text-sm">{deposit.description}</p>
                    <p className="text-xs text-gray-500">{deposit.date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">
                      ${deposit.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                    {deposit.matched && <Badge variant="success">Matched</Badge>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* POS Batches & Delivery Payouts */}
        <div className="space-y-6">
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">POS Batches</h2>
            <div className="space-y-2">
              {posBatches.map((batch) => (
                <div
                  key={batch.id}
                  onClick={() => !batch.matched && setSelectedSource(batch.id)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                    batch.matched
                      ? 'bg-green-50 border-green-200 opacity-50 cursor-not-allowed'
                      : selectedSource === batch.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <IntegrationBadge platform={batch.provider} size="sm" />
                        <span className="text-xs text-gray-400 font-mono">{batch.batchId}</span>
                      </div>
                      <p className="text-xs text-gray-500">{batch.date} • {batch.txnCount} transactions</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        Net: ${batch.netAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-gray-500">
                        Gross: ${batch.grossAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                      {batch.matched && <Badge variant="success">Matched</Badge>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Delivery Payouts</h2>
            <div className="space-y-2">
              {deliveryPayouts.map((payout) => (
                <div
                  key={payout.id}
                  onClick={() => !payout.matched && setSelectedSource(payout.id)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                    payout.matched
                      ? 'bg-green-50 border-green-200 opacity-50 cursor-not-allowed'
                      : selectedSource === payout.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <IntegrationBadge platform={payout.platform} size="sm" />
                      </div>
                      <p className="text-xs text-gray-400 font-mono">{payout.payoutId}</p>
                      <p className="text-xs text-gray-500">{payout.date} • {payout.orderCount} orders</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        Net: ${payout.netAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-gray-500">
                        Gross: ${payout.grossAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
